import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAtlasStore } from '../../../store/atlasStore';
import { TagFacetDimension, SparkLibrarySearchResponse } from '../types/sparkTypes';
import { SearchToolbar } from '../components/search/SearchToolbar';
import { Results, DelayedLoader } from '../components/search/Results';
import styles from '../styles/InboxView.module.css';
import { CreativeEditor } from '../components/modals/CreativeEditor';
import boardStyles from '../styles/CreativeBoard.module.css';
import CompareItemsModal from '../components/modals/CompareItemsModal';

type LocationState = { creativeTypeFqn?: string } | null;

export const CreativeSearchView: React.FC = () => {
  const searchLibrary = useAtlasStore(s => s.searchLibraryItems);
  const fetchFacets = useAtlasStore(s => s.fetchLibraryFacets);
  const creativeTypes = useAtlasStore(s => s.sparkCreativeTypes);
  const fetchCreativeTypes = useAtlasStore(s => s.fetchSparkCreativeTypes);
  const isLoadingCreativeTypes = useAtlasStore(s => s.isLoadingSpark.creativeTypes);
  const location = useLocation();

  const [q, setQ] = useState('');
  const [sortField, setSortField] = useState<'name' | 'publishedAt'>('publishedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const viewMode = 'list' as const;
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [facets, setFacets] = useState<TagFacetDimension[]>([]);
  const [selectedTagsByDim, setSelectedTagsByDim] = useState<Record<string, string[]>>({});
  const [results, setResults] = useState<SparkLibrarySearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewItemFqn, setPreviewItemFqn] = useState<string | null>(null);
  // Selection state for compare
  const [selectedItemFqns, setSelectedItemFqns] = useState<string[]>([]);
  const [isCompareOpen, setIsCompareOpen] = useState(false);
  const [hasResolvedCreativeTypes, setHasResolvedCreativeTypes] = useState<boolean>((creativeTypes?.length || 0) > 0);
  const hasRequestedCreativeTypesRef = useRef<boolean>(false);

  // Allow binding the search to a specific creative type via route state
  const boundCreativeTypeFqn = useMemo(() => {
    const state = (location.state as LocationState) || {};
    return state.creativeTypeFqn || undefined;
  }, [location.state]);

  // Ensure creative types are available for display naming
  useEffect(() => {
    if (creativeTypes && creativeTypes.length > 0) {
      hasRequestedCreativeTypesRef.current = false;
      setHasResolvedCreativeTypes(true);
      return;
    }
    if (!hasRequestedCreativeTypesRef.current) {
      hasRequestedCreativeTypesRef.current = true;
      fetchCreativeTypes()
        .catch(() => {/* non-fatal */ })
        .finally(() => setHasResolvedCreativeTypes(true));
      return;
    }
    if (!isLoadingCreativeTypes) {
      setHasResolvedCreativeTypes(true);
    }
  }, [creativeTypes, fetchCreativeTypes, isLoadingCreativeTypes]);

  const fallbackTypeLabel = useCallback((fqn: string) => {
    const base = fqn.includes('@') ? fqn.split('@')[1] : fqn;
    const token = base.split('/').pop() || base;
    const spaced = token.replace(/[_-]+/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }, []);

  const libraryTypes = useMemo(
    () => (creativeTypes || []).filter(ct => ct.library).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [creativeTypes],
  );
  const hasEnabledLibraryTypes = libraryTypes.length > 0;
  const showTypeSelector = libraryTypes.length > 1;
  const defaultTypeFilterValue = useMemo(() => {
    if (boundCreativeTypeFqn && libraryTypes.some(t => t.fqn === boundCreativeTypeFqn)) {
      return boundCreativeTypeFqn;
    }
    if (!showTypeSelector && libraryTypes[0]) {
      return libraryTypes[0].fqn;
    }
    return 'all';
  }, [boundCreativeTypeFqn, libraryTypes, showTypeSelector]);

  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>(defaultTypeFilterValue);

  useEffect(() => {
    setSelectedTypeFilter(defaultTypeFilterValue);
  }, [defaultTypeFilterValue]);

  useEffect(() => {
    setSelectedTypeFilter(prev => {
      if (prev === 'all') {
        return prev;
      }
      if (!libraryTypes.some(t => t.fqn === prev)) {
        if (showTypeSelector) {
          return 'all';
        }
        return libraryTypes[0]?.fqn ?? 'all';
      }
      return prev;
    });
  }, [libraryTypes, showTypeSelector]);

  const typeFilterFqn = useMemo(() => {
    if (selectedTypeFilter === 'all') {
      return showTypeSelector ? undefined : libraryTypes[0]?.fqn;
    }
    return selectedTypeFilter;
  }, [selectedTypeFilter, showTypeSelector, libraryTypes]);
  const selectionEnabled = Boolean(typeFilterFqn);

  const typeFilterOptions = useMemo(() => {
    if (!showTypeSelector) {
      return [];
    }
    return [
      { value: 'all', label: 'All' },
      ...libraryTypes.map((t) => ({
        value: t.fqn,
        label: t.plural_name || t.name || fallbackTypeLabel(t.fqn),
      })),
    ];
  }, [fallbackTypeLabel, libraryTypes, showTypeSelector]);

  // Tab indicator refs & state (same pattern as InboxView)
  const scopeTabsRef = useRef<HTMLDivElement>(null);
  const scopeTabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [scopeIndicatorStyle, setScopeIndicatorStyle] = useState({ left: 0, width: 0 });

  useEffect(() => {
    if (!showTypeSelector) return;
    const updateIndicator = () => {
      const container = scopeTabsRef.current;
      const activeEl = scopeTabRefs.current.get(selectedTypeFilter);
      if (!container || !activeEl) return;
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeEl.getBoundingClientRect();
      setScopeIndicatorStyle({
        left: tabRect.left - containerRect.left,
        width: tabRect.width,
      });
    };
    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [selectedTypeFilter, showTypeSelector]);

  const tagsParam = useMemo(() => {
    const entries: string[] = [];
    Object.entries(selectedTagsByDim).forEach(([dim, elems]) => {
      elems.forEach(e => entries.push(`${dim}:${e}`));
    });
    return entries;
  }, [selectedTagsByDim]);

  // Use refs so search/facet callbacks always read the latest values
  const tagsParamRef = useRef(tagsParam);
  tagsParamRef.current = tagsParam;
  const typeFilterFqnRef = useRef(typeFilterFqn);
  typeFilterFqnRef.current = typeFilterFqn;

  // Track latest requests to avoid stale updates
  const latestSearchIdRef = useRef(0);
  const latestFacetsIdRef = useRef(0);

  const runSearch = useCallback(async (pageArg?: number) => {
    const requestId = ++latestSearchIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const effectivePage = pageArg ?? page;
      const params: any = {
        q, tags: tagsParamRef.current, page: effectivePage, page_size: pageSize,
        sort: sortField, dir: sortDir, state: 'published',
      };
      if (typeFilterFqnRef.current) {
        params.creative_type_fqn = typeFilterFqnRef.current;
      }
      const data = await searchLibrary(params);
      if (requestId === latestSearchIdRef.current) {
        setResults(data);
      }
    } catch (e: any) {
      if (requestId === latestSearchIdRef.current) {
        setError(e?.message || 'Search failed');
      }
    } finally {
      if (requestId === latestSearchIdRef.current) {
        setLoading(false);
      }
    }
  }, [q, page, pageSize, sortField, sortDir, searchLibrary]);

  const loadFacets = useCallback(async () => {
    const requestId = ++latestFacetsIdRef.current;
    try {
      const params: any = { q, tags: tagsParamRef.current, state: 'published' as const };
      if (typeFilterFqnRef.current) {
        params.creative_type_fqn = typeFilterFqnRef.current;
      }
      const { dimensions } = await fetchFacets(params);
      if (requestId === latestFacetsIdRef.current) {
        setFacets(dimensions || []);
      }
    } catch {
      // Non-fatal for UI; keep previous facets
    }
  }, [fetchFacets, q]);

  // Initial load: hydrate facets; search runs via sortDir effect
  useEffect(() => {
    loadFacets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-run search when sort changes
  useEffect(() => {
    runSearch(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDir]);

  // Re-run when type filter or tag selection changes
  useEffect(() => {
    const firstPage = 1;
    setPage(firstPage);
    setSelectedItemFqns([]);
    loadFacets();
    runSearch(firstPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilterFqn, tagsParam]);

  const onToggleTag = (dimensionFqn: string, elementFqn: string) => {
    setSelectedTagsByDim(prev => {
      const current = new Set(prev[dimensionFqn] || []);
      if (current.has(elementFqn)) current.delete(elementFqn); else current.add(elementFqn);
      return { ...prev, [dimensionFqn]: Array.from(current) };
    });
  };

  const onSearchClick = async () => {
    const firstPage = 1;
    setPage(firstPage);
    setSelectedItemFqns([]);
    await loadFacets();
    await runSearch(firstPage);
  };

  const onToggleSort = () => setSortDir(prev => (prev === 'desc' ? 'asc' : 'desc'));

  const totalPages = useMemo(() => results ? Math.max(1, Math.ceil(results.total / results.page_size)) : 1, [results]);
  const goToPage = async (p: number) => {
    const next = Math.min(Math.max(1, p), totalPages);
    setPage(next);
    await runSearch(next);
  };

  if (hasResolvedCreativeTypes && !hasEnabledLibraryTypes) {
    return <Navigate to="/spark" replace />;
  }

  // When previewing an item, render the editor as the full view
  if (previewItemFqn) {
    return (
      <CreativeEditor
        isOpen={true}
        onClose={async () => {
          await loadFacets();
          await runSearch(page);
          setPreviewItemFqn(null);
        }}
        editorMode={'item'}
        itemFqn={previewItemFqn}
        onDataRefreshed={async () => { await runSearch(page); }}
      />
    );
  }

  // Compare overlay (full view replacement)
  if (isCompareOpen) {
    return (
      <CompareItemsModal
        isOpen={true}
        onClose={() => setIsCompareOpen(false)}
        itemFqns={selectedItemFqns}
      />
    );
  }

  return (
    <div className={styles.inboxViewContainer}>
      <div className={styles.mainContentWrapper}>
        <header className={styles.header}>
          <h1>Search Innovation</h1>
        </header>

        {showTypeSelector && (
          <div className={styles.scopeTabs} ref={scopeTabsRef}>
            {typeFilterOptions.map(option => (
              <button
                key={option.value}
                type="button"
                className={`${styles.scopeTab} ${selectedTypeFilter === option.value ? styles.scopeTabActive : ''}`}
                onClick={() => setSelectedTypeFilter(option.value)}
                ref={(el) => {
                  if (el) scopeTabRefs.current.set(option.value, el);
                  else scopeTabRefs.current.delete(option.value);
                }}
              >
                {option.value === 'all' ? 'All' : option.label}
              </button>
            ))}
            <div
              className={styles.scopeTabIndicator}
              style={{ left: scopeIndicatorStyle.left, width: scopeIndicatorStyle.width }}
            />
          </div>
        )}
        <div className={styles.content} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <SearchToolbar
            q={q}
            onChangeQ={setQ}
            facets={facets}
            selected={selectedTagsByDim}
            onToggleTag={onToggleTag}
            sortField={sortField}
            sortDir={sortDir}
            onChangeSort={(field, dir) => {
              setSortField(field);
              setSortDir(dir);
            }}
            onToggleSort={onToggleSort}
            onSearch={onSearchClick}
            loading={loading}
            onClearAll={() => {
              setSelectedTagsByDim({});
            }}
            selectedCompareCount={selectionEnabled ? selectedItemFqns.length : 0}
            onClearSelection={() => {
              setSelectedItemFqns([]);
            }}
            rightActions={
              (() => {
                const selCount = selectedItemFqns.length;
                const enabled = selectionEnabled && (selCount === 2 || selCount === 3);
                const tooltip = !selectionEnabled
                  ? 'Select a creative type to compare items'
                  : (enabled ? 'Compare selected items' : 'Select 2–3 items to compare');
                return (
                  <button
                    className={`${boardStyles.actionButton} ${boardStyles.newButton}`}
                    onClick={() => enabled && setIsCompareOpen(true)}
                    title={tooltip}
                    disabled={!enabled}
                  >
                    Compare
                  </button>
                );
              })()
            }
          />

          {/* Scrollable results area under the toolbar */}
          <section style={{ marginTop: 12, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <DelayedLoader visible={loading} />
            {error && <div style={{ color: 'red' }}>{error}</div>}
            {!loading && results && (
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                  <Results
                    items={results.items}
                    viewMode={viewMode}
                    onOpen={(fqn) => setPreviewItemFqn(fqn)}
                    selectable={selectionEnabled}
                    hasActiveFilters={q.trim().length > 0 || tagsParam.length > 0}
                    selectedFqns={selectedItemFqns}
                    onToggleSelect={(fqn, checked) => {
                      if (!selectionEnabled) {
                        return;
                      }
                      setSelectedItemFqns(prev => {
                        if (checked) {
                          if (prev.includes(fqn)) return prev;
                          return [...prev, fqn];
                        }
                        return prev.filter(x => x !== fqn);
                      });
                    }}
                  />
                </div>
                {/* Centered pagination with page numbers */}
                {totalPages > 1 && (
                  <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                    {(() => {
                      const pages: (number | string)[] = [];
                      const cur = page;
                      const total = totalPages;
                      const start = Math.max(1, cur - 2);
                      const end = Math.min(total, cur + 2);
                      if (start > 1) {
                        pages.push(1);
                      }
                      if (start > 2) {
                        pages.push('…');
                      }
                      for (let p = start; p <= end; p++) {
                        pages.push(p);
                      }
                      if (end < total - 1) {
                        pages.push('…');
                      }
                      if (end < total) {
                        pages.push(total);
                      }
                      return (
                        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          <button
                            disabled={cur <= 1}
                            onClick={() => goToPage(cur - 1)}
                            style={{ border: 'none', background: 'transparent', cursor: cur <= 1 ? 'default' : 'pointer' }}
                            aria-label="Previous page"
                          >
                            {'<'}
                          </button>
                          {pages.map((item, idx) =>
                            typeof item === 'number' ? (
                              <button
                                key={`p-${item}-${idx}`}
                                onClick={() => goToPage(item)}
                                aria-current={item === cur ? 'page' : undefined}
                                aria-label={`Go to page ${item}`}
                                style={{
                                  minWidth: 32,
                                  padding: '4px 8px',
                                  borderRadius: 6,
                                  border: item === cur ? '1px solid #2E90FA' : '1px solid #D0D5DD',
                                  background: item === cur ? '#E6F0FE' : 'white',
                                  color: item === cur ? '#1D4ED8' : '#111827',
                                  cursor: 'pointer',
                                }}
                              >
                                {item}
                              </button>
                            ) : (
                              <span key={`e-${idx}`} style={{ padding: '0 4px', color: '#6B7280' }}>…</span>
                            )
                          )}
                          <button
                            disabled={cur >= total}
                            onClick={() => goToPage(cur + 1)}
                            style={{ border: 'none', background: 'transparent', cursor: cur >= total ? 'default' : 'pointer' }}
                            aria-label="Next page"
                          >
                            {'>'}
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};
